<script setup lang="ts">
import type { ProductPrice } from "@classes-service-types/index.ts";
import { useQuery } from "@tanstack/vue-query";
import { useVModel } from "@vueuse/core";
import { format } from "date-fns";
import { debounce, uniqBy } from "lodash";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import InputSwitch from "primevue/inputswitch";
import MultiSelect from "primevue/multiselect";
import { computed, ref, toRef, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import ProfileLeaderSelect from "@/components/forms/ProfileLeaders/ProfileLeaderSelect.vue";
import WYSIWYG from "@/components/forms/SharedComponents/WYSIWYG/WYSIWYG.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import { handleOperationFailure } from "@/components/modals/utils.js";
import { useGetCityOptions } from "@/components/program-manager/sessions/composable.js";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import CalendarInput from "@/components/shared/CalendarInput.vue";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import TimezoneSelect from "@/components/shared/select/TimezoneSelect/index.vue";
import { hasPublishPermission } from "@/composables/authorization.js";
import { EVENTS_STATUS, LIBRARY_ID } from "@/constants.js";
import citiesService from "@/services/cities.js";
import eventsService from "@/services/events.js";
import productPriceService from "@/services/productPrices";
import AddressComponent from "../../forms/AddressInputComponent/AddressComponent.vue";
import { useOccurrenceDateTimes } from "./composables/useOccurrenceDateTimes";
import { useOccurrenceProfiles } from "./composables/useOccurrenceProfiles";
import { useOccurrenceValidation } from "./composables/useOccurrenceValidation";
import SectionHeader from "./SectionHeader.vue";
import { checkboxPt, dialogPt, multiSelectPt } from "./style";
import ToggleSetting from "./ToggleSetting.vue";
import type { CreateEditOccurrenceModalProps, OccurrenceModelValue } from "./types";
import ValidationErrors from "./ValidationErrors.vue";

const store = useStore();

const props = withDefaults(defineProps<CreateEditOccurrenceModalProps>(), {
  edit: false,
  open: false,
  eventName: "Event Name",
  location: "Event Location",
  defaultTimeZone: 0,
  modelValue: () => ({}) as OccurrenceModelValue,
  isOnline: false,
  enableModal: true,
});
const title = computed<string>(() => `${!props.edit ? "Create" : "Edit"} Occurrence`);
const emit = defineEmits<{
  (e: "update:modelValue", value: OccurrenceModelValue): void;
  (e: "closeModal", saved: boolean): void;
  (e: "openModal"): void;
  (e: "validator", validateFn: () => Promise<boolean>): void;
}>();

const occurrence = useVModel(props, "modelValue", emit);

const validateTab = async (): Promise<boolean> => {
  return v$.value.$validate().then(() => {
    return !v$.value.$invalid;
  });
};

emit("validator", validateTab);

const productPrice = ref<Partial<ProductPrice>>({});
const saveSuccessful = ref<boolean>(false);
const toast = useToast();

const disabledDraftButton = computed(() => occurrence.value.status_id === EVENTS_STATUS.active.id);
const priceStudent = ref<number>((occurrence.value.price_student ?? 0) / 100 || 0);
const pendingStatusId = ref<string | undefined>(occurrence.value.status_id);

const isLoading = ref<boolean>(false);
const activeDateTimeBeforeToday = ref<boolean>(false);

const openModal = (): void => {
  if (props.enableModal) emit("openModal");
};

watch(
  () => props.open,
  () => {
    if (props.open) {
      setStatus();
      setInitialDatesTimes();
      setInitialLeaders();
      priceStudent.value = (occurrence.value.price_student ?? 0) / 100 || 0;
      pendingStatusId.value = occurrence.value.status_id;
    }
  },
);

const closeModal = (): void => {
  const saved = saveSuccessful.value;
  startDateTime.date = "";
  startDateTime.time = "";
  endDateTime.date = "";
  endDateTime.time = "";
  registrationDateTime.date = "";
  registrationDateTime.time = "";
  timeZone.value = undefined;
  activeDateTimeBeforeToday.value = false;
  productPrice.value = {};
  saveSuccessful.value = false;
  v$.value.$reset();
  emit("closeModal", saved);
};

const normalizeCapacity = (value: number | string | null | undefined): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeCapacities = (): void => {
  occurrence.value.max_capacity = normalizeCapacity(occurrence.value.max_capacity);
  occurrence.value.min_capacity = normalizeCapacity(occurrence.value.min_capacity);
};

const saveDraft = async (): Promise<void> => {
  saveSuccessful.value = false;
  pendingStatusId.value = occurrence.value.status_id;
  v$.value.$touch();
  const isValid = await v$.value.$validate();
  if (isValid) {
    isLoading.value = true;
    updateDateTimes();
    removeEmptyLeaders();
    normalizeCapacities();
    if (props.edit) {
      await updateOccurrence();
    } else {
      await createOccurrence();
    }
    isLoading.value = false;
    verifySaveAndClose();
  }
};

const publish = async (): Promise<void> => {
  saveSuccessful.value = false;
  pendingStatusId.value = EVENTS_STATUS.active.id;
  v$.value.$touch();
  const isValid = await v$.value.$validate();
  if (isValid) {
    occurrence.value.occurrence_status = EVENTS_STATUS.active;
    occurrence.value.status_id = EVENTS_STATUS.active.id;
    isLoading.value = true;
    updateDateTimes();
    removeEmptyLeaders();
    normalizeCapacities();
    if (props.edit) {
      await setProductPrice();
      await updateOccurrence();
    } else {
      await createOccurrence();
    }
    await createOrUpdateProductPrice();
    isLoading.value = false;
    verifySaveAndClose();
  }
};

const verifySaveAndClose = (): void => {
  if (saveSuccessful.value) {
    closeModal();
    toast.open({
      message: "Occurrence Saved Successfully",
      type: "success",
      position: "bottom",
      duration: 5000,
    });
  } else {
    toast.open({
      message: "Failed to save Occurrence",
      type: "info",
      position: "bottom",
      duration: 5000,
    });
  }
};

const createOccurrence = async (): Promise<void> => {
  occurrence.value.price_student = priceStudent.value * 100;

  try {
    const createResponse = await eventsService.createOccurrence(occurrence.value);

    if (createResponse.status === 200 && createResponse.data.data) {
      saveSuccessful.value = true;
      Object.assign(occurrence.value, createResponse.data.data);
    }
  } catch (error) {
    handleOperationFailure("Failed to create occurrence", error);
  }
};

const updateOccurrence = async (): Promise<void> => {
  occurrence.value.price_student = priceStudent.value * 100;

  try {
    const updateResponse = await eventsService.updateOccurrence(occurrence.value.id, occurrence.value);
    if (updateResponse.status === 200 && updateResponse.data.data) {
      saveSuccessful.value = true;
      Object.assign(occurrence.value, updateResponse.data.data);
    }
  } catch (error) {
    toast.open({
      message: "Failed to update",
      type: "info",
      position: "bottom",
      duration: 5000,
    });
  }
};

const createOrUpdateProductPrice = async (): Promise<void> => {
  if (!saveSuccessful.value) return;

  if (!!productPrice.value?.id && occurrence.value.price_student) {
    await updateProductPrice().catch((error) => {
      handleOperationFailure("Failed to update product price", error);
    });
  } else {
    await createProductPrice().catch((error) => {
      handleOperationFailure("Failed to create product price", error);
    });
  }
};

const createProductPrice = async (): Promise<void> => {
  try {
    const response = await productPriceService.createProductPrice({
      product_id: occurrence.value.id,
      libraries_id: LIBRARY_ID,
      price_in_cents: occurrence.value.price_student,
    });
    if (response.status === 200) {
      saveSuccessful.value = true;
    } else {
      throw new Error("Product price creation failed");
    }
  } catch (error) {
    saveSuccessful.value = false;
    throw new Error("Product price creation failed");
  }
};

const updateProductPrice = async (): Promise<void> => {
  try {
    const response = await productPriceService.updateProductPrice(productPrice.value.id, {
      id: productPrice.value.id,
      product_id: occurrence.value.id,
      libraries_id: LIBRARY_ID,
      price_in_cents: occurrence.value.price_student,
    });
    if (response.status === 200) {
      saveSuccessful.value = true;
    } else {
      throw new Error("Product price update failed");
    }
  } catch (error) {
    saveSuccessful.value = false;
    throw new Error("Product price update failed");
  }
};

const setProductPrice = async (): Promise<void> => {
  try {
    const {
      data: {
        data: { items },
      },
    } = await productPriceService.getProductPrice(occurrence.value.id);
    productPrice.value = items[0];
  } catch (e) {
    // No Product price, do nothing.
  }
};

// city
const searchCity = ref<string>("");
const city = ref<any>("");
const cityExtraInformation = ref<Record<string, any>>(city.value ? city.value : {});

const enableGetCitySaved = computed<boolean>(() => !!occurrence.value?.departure_city);
const queryKey = computed(() => ["fetchCity", occurrence.value.departure_city]);
const { data: savedCity } = useQuery({
  queryKey,
  queryFn: () => {
    return citiesService.getSingleCity({ city_id: occurrence.value?.departure_city }).then((val) => val?.data?.data);
  },
  enabled: enableGetCitySaved,
});

const getSavedCity = computed(() => {
  return savedCity.value ? [savedCity.value] : [];
});

watch(searchCity, async () => {
  await fetchSearchableCitiesOptions(searchCity.value);
});

const setCityExtraInformation = (val: Record<string, any>): void => {
  cityExtraInformation.value = val;
};

const {
  execute: executeFetchCityOptions,
  isLoading: cityLoading,
  state: cityOptions,
  isReady: cityReady,
} = useGetCityOptions(
  {
    immediate: true,
    throwError: true,
  },
  {
    city_ascii: city.value?.city_ascii || "chi",
  },
);

const fetchSearchableCitiesOptions = debounce(async (search: string, loading?: (val: boolean) => void) => {
  if (search && search?.length > 2) {
    if (loading) loading(true);
    try {
      cityExtraInformation.value = {};
      await executeFetchCityOptions(0, {
        city_ascii: search,
        cancel: true,
      });
      if (loading) loading(false);
    } catch (e: any) {
      if (e?.message !== "cancel") {
        if (loading) loading(false);
      }
    }
  }
}, 500);
//end city

//Address
const getInitialAddressInfo = computed<Record<string, any>>(() => {
  return {
    street_address_1: occurrence.value?.occurrence_address?.street_address_1 || undefined,
    street_address_2: occurrence.value?.occurrence_address?.street_address_2 || undefined,
    city: occurrence.value?.occurrence_address?.city || undefined,
    state: occurrence.value?.occurrence_address?.state || undefined,
    iso: occurrence.value?.occurrence_address?.iso || undefined,
    number: occurrence.value?.occurrence_address?.number || undefined,
    country: occurrence.value?.occurrence_address?.country || undefined,
    postal_code: occurrence.value?.occurrence_address?.postal_code || undefined,
    utc_offset_minutes: occurrence.value?.occurrence_address?.utc_offset_minutes || undefined,
  };
});

const updateAddressInfo = (address: Record<string, any>): void => {
  const { lat, long } = address;
  occurrence.value.occurrence_address = address;
  occurrence.value.occurrence_address.event_lat_lng = `${lat || ""}, ${long || ""}`;
  setTimeZone(address.utc_offset_minutes, "offset", address.iso);
};
// end address

const {
  startDateTime,
  endDateTime,
  registrationDateTime,
  timeZone,
  updateDateTimes,
  setInitialDatesTimes,
  setTimeZone,
} = useOccurrenceDateTimes(occurrence, toRef(props, "defaultTimeZone"));
const { occurrenceProfilesLoading, addLeaderName, setInitialLeaders, deleteProfileLeader, removeEmptyLeaders } =
  useOccurrenceProfiles(occurrence);
const { v$ } = useOccurrenceValidation(
  occurrence,
  priceStudent,
  pendingStatusId,
  startDateTime,
  endDateTime,
  registrationDateTime,
  timeZone,
);

const setStatus = (): void => {
  if (!occurrence.value.status_id) {
    occurrence.value.occurrence_status = EVENTS_STATUS.draft;
    occurrence.value.status_id = EVENTS_STATUS.draft.id;
  }
};

const showMarketplaceToggle = computed<boolean>(() => {
  const featureFlags = store.state.featureFlags;
  return featureFlags["tenant-libraries"];
});

const getProgramTypes = computed(() => {
  return store.getters["events/getProgramTypes"];
});

const publishIsdAllowed = computed<boolean>(() => {
  return hasPublishPermission();
});
</script>
<template>
  <div v-if="!isLoading">
    <Dialog
      :visible="props.open"
      :modal="true"
      :draggable="false"
      :resizable="false"
      :closeOnEscape="false"
      :pt="dialogPt"
      @update:visible="closeModal"
    >
      <template #header>
        <h1 class="font-bold text-[17.5px] text-slate-700" data-test-id="occurrence-modal-title-label">{{ title }}</h1>
      </template>

      <div class="flex flex-col gap-6">
        <!-- Event Info Section -->
        <section class="flex flex-col gap-4">
          <SectionHeader title="Event info" />

          <div class="grid grid-cols-2 gap-6">
            <div>
              <span class="text-xs font-bold text-blue-darker">Event:</span>
              <span class="ml-3 text-sm text-indigo-base" data-test-id="event-name-label">{{ eventName }}</span>
            </div>
            <div>
              <span class="text-xs font-bold text-blue-darker">Location:</span>
              <span class="ml-3 text-sm text-indigo-base" data-test-id="event-location-label">{{ location }}</span>
            </div>
          </div>
        </section>

        <!-- Schedule Section -->
        <section class="flex flex-col gap-4">
          <SectionHeader title="Schedule" />

          <div class="flex items-center gap-4 p-3 bg-blue-100 border border-gray-175 rounded-lg">
            <div class="flex-1">
              <div class="font-bold text-sm text-blue-darker">Asynchronous event</div>
              <div class="text-xs text-[#787D91] mt-1">No fixed start or end — participants take part on their own schedule.</div>
            </div>
            <div class="flex">
              <InputSwitch v-model="occurrence.is_asynchronous" data-test-id="is-asynchronous-input"/>
            </div>
          </div>

          <div class="grid grid-cols-[164px_1fr_1fr] gap-x-6 gap-y-2 items-start">
            <div
              class="pt-8 text-xs font-bold text-blue-darker"
            >
              Registration Deadline:
            </div>
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">Date</label>
              <CalendarInput
                name="startDate"
                :model-value="registrationDateTime.date"
                input-classes="h-10 w-full rounded-md border-gray-300 border shadow-sm sm:text-sm pl-2 custom-date-picker"
                data-test-id="registration-deadline-date-input"
                @on-change="(date) => registrationDateTime.date = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : ''"
              />
            </div>
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">Time</label>
              <input
                v-model="registrationDateTime.time"
                type="time"
                class="w-full h-10 px-3 border border-gray-300 rounded text-sm"
                :class="{ 'border-red-700': v$.occurrence.registration_deadline.$errors?.length }"
                data-test-id="registration-deadline-time-input"
                @blur="v$.occurrence.registration_deadline.$touch"
              />
              <ValidationErrors :errors="v$.occurrence.registration_deadline.$errors" data-test-id="registration-deadline-error-section" />
            </div>
          </div>

          <div
            v-if="!occurrence.is_asynchronous"
            class="grid grid-cols-[164px_1fr_1fr] gap-x-6 gap-y-2 items-start"
          >
            <div class="pt-8 text-xs font-bold text-blue-darker">Start Date & Time:</div>
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">Date</label>
              <CalendarInput
                name="startDate"
                :model-value="startDateTime.date"
                :class="{ 'border-red-700': v$.occurrence.start_date_time.$errors?.length }"
                input-classes="h-10 w-full rounded-md border-gray-300 border shadow-sm sm:text-sm pl-2 custom-date-picker"
                data-test-id="start-date-input"
                @on-change="(date) => startDateTime.date = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : ''"
                @blur="v$.occurrence.start_date_time.$touch"
              />
              <ValidationErrors :errors="v$.occurrence.start_date_time.$errors" data-test-id="start-date-error-section" />
            </div>
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">Time</label>
              <input
                v-model="startDateTime.time"
                type="time"
                class="w-full h-10 px-3 border border-gray-300 rounded text-sm"
                :class="{ 'border-red-700': v$.occurrence.start_time.$errors?.length }"
                data-test-id="start-time-input"
                @blur="v$.occurrence.start_time.$touch"
              />
              <ValidationErrors :errors="v$.occurrence.start_time.$errors" data-test-id="start-time-error-section" />
            </div>
            <label class="flex items-center gap-2 col-start-2">
              <Checkbox v-model="occurrence.start_date_time_is_final" binary :pt="checkboxPt" data-test-id="start-date-time-is-final-input"/>
              <span class="text-sm text-indigo-base">Start date & time is final</span>
            </label>
          </div>

          <div
            v-if="!occurrence.is_asynchronous"
            class="grid grid-cols-[164px_1fr_1fr] gap-x-6 gap-y-2 items-start"
          >
            <div class="pt-8 text-xs font-bold text-blue-darker">End Date & Time:</div>
            <div>
              <label class="block text-xs font-bold text-slate-700 mb-2">Date</label>
              <CalendarInput
                name="endDate"
                :model-value="endDateTime.date"
                :class="{ 'border-red-700': v$.occurrence.end_date_time.$errors?.length }"
                input-classes="h-10 w-full rounded-md border-gray-300 border shadow-sm sm:text-sm pl-2 custom-date-picker"
                data-test-id="end-date-input"
                @on-change="(date) => endDateTime.date = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : ''"
                @blur="v$.occurrence.end_date_time.$touch"
              />
              <ValidationErrors :errors="v$.occurrence.end_date_time.$errors" data-test-id="end-date-error-section" />
            </div>
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">Time</label>
              <input
                v-model="endDateTime.time"
                type="time"
                class="w-full h-10 px-3 border border-gray-300 rounded text-sm"
                :class="{ 'border-red-700': v$.occurrence.end_time.$errors?.length }"
                data-test-id="end-time-input"
                @blur="v$.occurrence.end_time.$touch"
              />
              <ValidationErrors :errors="v$.occurrence.end_time.$errors" data-test-id="end-time-error-section" />
            </div>
            <label class="flex items-center gap-2 col-start-2">
              <Checkbox v-model="occurrence.end_date_time_is_final" binary :pt="checkboxPt" data-test-id="end-date-time-is-final-input"/>
              <span class="text-sm text-indigo-base">End date & time is final</span>
            </label>
          </div>

          <div>
            <label class="block text-xs font-bold text-blue-darker mb-2">Time Zone</label>
            <TimezoneSelect
              v-model="timeZone"
              :invalid="!!v$.occurrence.time_zone.$errors.length"
              data-test-id="timezone-input"
            />
            <ValidationErrors :errors="v$.occurrence.time_zone.$errors" data-test-id="timezone-error-section" />
          </div>
        </section>

        <!-- Event Details Section -->
        <section class="flex flex-col gap-4">
          <SectionHeader title="Event Details" />

          <div class="flex gap-4">
            <div class="flex-1">
              <label class="block text-xs font-bold text-blue-darker mb-2">Event Leader Name</label>
              <template v-if="occurrence.occurrence_profiles">
                <div class="w-full flex flex-row mb-3" v-for="(profile, index) in occurrence.occurrence_profiles" :key="profile.id">
                  <ProfileLeaderSelect
                    v-model="occurrence.occurrence_profiles[index]"
                    @delete-item="deleteProfileLeader(index)"
                    :has-role="false"
                    :display-label="false"
                    :parent-loading="occurrenceProfilesLoading"
                  />
                </div>
              </template>
              <button @click="addLeaderName" class="mt-3 flex flex-row items-center text-center" data-test-id="add-leader-button">
                <PlusIcon class="mr-1 w-5 h-5 text-blue-darker" />
                <span class="text-sm font-bold text-teal-600 hover:underline">Add another leader</span>
              </button>
            </div>
            <div class="flex-1">
              <label class="block text-xs font-bold text-blue-darker mb-2">
                <span class="text-red-700">*</span> Price Per Student
              </label>
              <div class="relative">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-indigo-base">$ USD</span>
                <input
                  v-model="priceStudent"
                  type="number"
                  placeholder="0.00"
                  class="block w-full pl-16 h-10 px-3 border border-gray-300 rounded text-sm"
                  :class="{ 'border-red-700': v$.occurrence.price_student.$errors?.length }"
                  :disabled="!publishIsdAllowed"
                  data-test-id="price-student-input"
                  @blur="v$.occurrence.price_student.$touch"
                />
              </div>
              <ValidationErrors :errors="v$.occurrence.price_student.$errors" data-test-id="price-student-error-section" />
            </div>
          </div>

          <div>
            <label class="block text-xs font-bold text-blue-darker mb-2">Meeting Point</label>
            <AddressComponent
              id="occurrence.occurrence_address"
              class="!ring-gray-300 !focus:ring-gray-300 rounded"
              template-name="v3"
              :initial-address-info="occurrence.occurrence_address ? getInitialAddressInfo : undefined"
              data-test-id="meeting-point-input"
              @update-address-info="(address) => updateAddressInfo(address)"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-blue-darker mb-2">Itinerary</label>
            <WYSIWYG
              v-model="occurrence.itinerary"
              :character-limit="4999"
              data-test-id="itinerary-input"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-blue-darker mb-2">Housing Type</label>
            <input
              v-model="occurrence.accommodation"
              type="text"
              placeholder="e.g., Hotel, Hostel..."
              class="w-full h-10 px-3 border border-gray-300 rounded text-sm"
              data-test-id="housing-type-input"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-blue-darker mb-2">Housing Description</label>
            <WYSIWYG
              v-model="occurrence.accommodation_description"
              :character-limit="4999"
              data-test-id="housing-description-input"
            />
          </div>

        </section>

        <!-- Capacity Section -->
        <section class="flex flex-col gap-4">
          <SectionHeader title="Capacity" />
          <div class="grid grid-cols-2 gap-6">
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">
                Max Participants
              </label>
              <input
                v-model="occurrence.max_capacity"
                type="number"
                placeholder="0"
                class="w-full h-10 px-3 border border-gray-300 rounded text-sm"
                :class="{ 'border-red-700': v$.occurrence.max_capacity.$errors?.length }"
                data-test-id="max-capacity-input"
                @blur="v$.occurrence.max_capacity.$touch"
              />
              <ValidationErrors :errors="v$.occurrence.max_capacity.$errors" data-test-id="max-capacity-error-section" />
            </div>
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">
                Min Participants Required
              </label>
              <input
                v-model="occurrence.min_capacity"
                type="number"
                placeholder="0"
                class="w-full h-10 px-3 border border-gray-300 rounded text-sm"
                :class="{ 'border-red-700': v$.occurrence.min_capacity.$errors?.length }"
                data-test-id="min-capacity-input"
                @blur="v$.occurrence.min_capacity.$touch"
              />
              <ValidationErrors :errors="v$.occurrence.min_capacity.$errors" data-test-id="min-capacity-error-section" />
            </div>
          </div>
        </section>

        <!-- Settings Section -->
        <section class="flex flex-col gap-4">
          <SectionHeader title="Settings" />

          <div class="grid grid-cols-2 gap-6">
            <ToggleSetting v-if="showMarketplaceToggle" label="Available in marketplace" v-model="occurrence.marketplace" data-test-id="marketplace-input"/>
            <ToggleSetting label="Collect RSVPs" description="Recipients can say if they're attending" v-model="occurrence.collect_rsvp" data-test-id="collect-rsvp-input"/>
            <ToggleSetting label="Allow guests to bring additional guests" description="Includes the recipient in the count" v-model="occurrence.guests_allowed" data-test-id="guests-allowed-input"/>
            <ToggleSetting label="Attendance required" v-model="occurrence.attendance_required" data-test-id="attendance-required-input"/>
            <div class="col-start-1">
              <label class="block text-xs font-bold text-blue-darker mb-2">Program Type</label>
              <MultiSelect
                v-model="occurrence.program_types"
                :options="getProgramTypes"
                option-label="name"
                placeholder="Select program types"
                display="chip"
                filter
                class="w-full"
                :pt="multiSelectPt"
                data-test-id="program-type-input"
              />
            </div>
            <div>
              <label class="block text-xs font-bold text-blue-darker mb-2">Departure City</label>
              <SearchableSelect
                v-model="occurrence.departure_city"
                :options="uniqBy((cityOptions?.items || []).concat(getSavedCity), 'id') || getSavedCity"
                :loading="cityLoading || !cityReady"
                :reduce="(val) => val.id"
                :deselect-from-dropdown="true"
                :close-on-select="true"
                :filterable="false"
                :clear-search-on-blur="() => false"
                label="city_ascii"
                data-test-id="departure-city-input"
                @search="fetchSearchableCitiesOptions"
                @option:selected="(val) => setCityExtraInformation(val)"
              >
                <template #option="{ city_ascii, country, admin_name_ascii }">
                  <div class="text-sm">
                    <div class="font-semibold">{{ city_ascii }}</div>
                    <div>{{ country?.name }}<span v-if="country?.name && admin_name_ascii">-</span>{{ admin_name_ascii }}</div>
                  </div>
                </template>
              </SearchableSelect>
            </div>
          </div>

        </section>
      </div>

      <template #footer>
        <BaseButton
          outlined
          @click="closeModal"
          data-test-id="cancel-occurrence-button"
        >
          Cancel
        </BaseButton>
        <BaseButton
          :disabled="disabledDraftButton"
          @click="saveDraft"
          data-test-id="save-draft-occurrence-button"
        >
          Save as Draft
        </BaseButton>
        <BaseButton
          :disabled="!publishIsdAllowed"
          @click="publish"
          data-test-id="publish-occurrence-button"
        >
          Publish Occurrence
        </BaseButton>
      </template>
    </Dialog>
    <div
      class="flex flex-row items-center text-center text-teal-600"
      :class="`${
        props.enableModal
          ? 'text-gray-600 cursor-pointer'
          : 'text-gray-300 cursor-default'
      }`"
      :aria-disabled="props.enableModal"
      data-test-id="create-occurrence-button"
      @click="openModal"
    >
      <PlusIcon class="mr-1 w-6 h-6" />
      <small>NEW OCCURRENCE</small>
    </div>
  </div>
  <Spinner
    v-show="isLoading"
    class="m-auto w-full h-full !fixed opacity-75 bg-blue-100 !z-0"
  />
</template>
